import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import {
  Users, Calendar, MessageSquare, Megaphone, Baby, UserRound, HeartPulse, Heart,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis,
  Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const PIE_COLORS = ["#D96C4A", "#2E7D32", "#1E4631", "#5C6B61"];

export default function DashboardHome() {
  const { t } = useApp();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/stats/timeseries`, { params: { days: 14 } })
      .then((r) => setSeries(r.data.series || [])).catch(() => {});
  }, []);

  const cards = [
    { label: t.dash.total_patients, value: stats?.patients.total ?? "—", icon: Users, color: "#1E4631", testid: "stat-total-patients" },
    { label: t.dash.appts_today, value: stats?.appointments.today ?? "—", icon: Calendar, color: "#D96C4A", testid: "stat-appts-today" },
    { label: t.dash.sms_sent, value: stats?.sms_sent ?? "—", icon: MessageSquare, color: "#2E7D32", testid: "stat-sms-sent" },
    { label: t.dash.campaigns_sent, value: stats?.campaigns ?? "—", icon: Megaphone, color: "#E3A72F", testid: "stat-campaigns" },
  ];

  const cats = [
    { label: t.dash.pregnant, value: stats?.patients.pregnant ?? 0, icon: Heart, color: "#D96C4A" },
    { label: t.dash.child, value: stats?.patients.child ?? 0, icon: Baby, color: "#2E7D32" },
    { label: t.dash.elderly, value: stats?.patients.elderly ?? 0, icon: UserRound, color: "#1E4631" },
    { label: t.dash.general, value: stats?.patients.general ?? 0, icon: HeartPulse, color: "#5C6B61" },
  ];

  const pieData = cats.map((c) => ({ name: c.label, value: c.value }));
  const chartData = series.map((s) => ({
    name: s.date.slice(5), // MM-DD
    Patients: s.patients,
    RDV: s.appointments,
    SMS: s.sms,
  }));
  const hasSeries = chartData.some((d) => d.Patients || d.RDV || d.SMS);

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.home}</h1>
        <p className="text-[#5C6B61] mt-1">{t.dash.overview}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c, i) => (
          <div key={i} data-testid={c.testid} className="bg-white rounded-2xl border border-[#E5EBE7] p-6 hover:shadow-md transition">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${c.color}15`, color: c.color }}>
                <c.icon className="w-5 h-5"/>
              </div>
            </div>
            <div className="mt-4 text-3xl font-[Outfit] font-bold">{c.value}</div>
            <div className="mt-1 text-sm text-[#5C6B61]">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="chart-activity">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-[Outfit] font-semibold text-lg">Activité des 14 derniers jours</h3>
            <span className="text-xs text-[#5C6B61]">Patients · RDV · SMS</span>
          </div>
          <div className="h-72">
            {hasSeries ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gPat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E4631" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#1E4631" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="gRdv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D96C4A" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#D96C4A" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="gSms" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#2E7D32" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7"/>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5C6B61" }} stroke="#E5EBE7"/>
                  <YAxis tick={{ fontSize: 11, fill: "#5C6B61" }} stroke="#E5EBE7" allowDecimals={false}/>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 12 }}/>
                  <Area type="monotone" dataKey="Patients" stroke="#1E4631" fill="url(#gPat)" strokeWidth={2}/>
                  <Area type="monotone" dataKey="RDV" stroke="#D96C4A" fill="url(#gRdv)" strokeWidth={2}/>
                  <Area type="monotone" dataKey="SMS" stroke="#2E7D32" fill="url(#gSms)" strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-[#5C6B61]">
                Pas encore d'activité sur les 14 derniers jours.
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="chart-categories">
          <h3 className="font-[Outfit] font-semibold text-lg mb-4">Répartition patients</h3>
          <div className="h-72">
            {pieData.some((d) => d.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5EBE7", fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-[#5C6B61]">Aucun patient enregistré.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
        <h3 className="font-[Outfit] font-semibold text-xl mb-6">{t.dash.by_category}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.map((c, i) => (
            <div key={i} className="p-5 rounded-xl border border-[#E5EBE7] hover:border-[#D96C4A] transition">
              <c.icon className="w-5 h-5" style={{ color: c.color }}/>
              <div className="mt-3 text-2xl font-[Outfit] font-bold">{c.value}</div>
              <div className="text-xs text-[#5C6B61] mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
